<script setup lang="ts">
import { computed, watchEffect } from "vue";
import { useRoute } from "vue-router";
import AppIcon from "@/components/AppIcon.vue";
import BrandMark from "@/components/BrandMark.vue";
import ThemePicker from "@/components/ThemePicker.vue";
import { useAuthStore } from "@/stores/auth";
import { useI18n } from "@/i18n";

const route = useRoute();
const auth = useAuthStore();
const { t } = useI18n();
// Pages refresh their localized API data in place so drafts and active games survive.
watchEffect(() => { document.title = `${t(String(route.meta.title ?? "GameGlee"))} · GameGlee`; });
const fullscreen = computed(() => Boolean(route.meta.fullscreen));
const activeSection = computed(() => {
  if (["settings", "control-settings", "safety", "history", "favorites", "friends", "membership"].includes(String(route.name))) return "profile";
  if (["detail", "search", "reviews", "saves"].includes(String(route.name))) return "library";
  return route.name;
});
const nav = [
  { name: "home", label: "Home", icon: "home" },
  { name: "library", label: "Library", icon: "grid" },
  { name: "download", label: "Download", icon: "download" },
  { name: "inbox", label: "Inbox", icon: "inbox" },
  { name: "profile", label: "Profile", icon: "user" },
];
const shortcuts = [
  { name: "history", label: "Recently played", icon: "clock" },
  { name: "favorites", label: "Saved games", icon: "heart" },
  { name: "friends", label: "Friends", icon: "user" },
];
</script>

<template>
  <div class="app" :class="{ fullscreen }">
    <a v-if="!fullscreen" class="skip-link" href="#main-content">{{ t('Skip to content') }}</a>
    <aside v-if="!fullscreen" class="side-nav">
      <RouterLink class="brand-home" :to="{ name: 'home' }" :aria-label="t('GameGlee home')"><BrandMark /></RouterLink>
      <nav :aria-label="t('Primary navigation')">
        <RouterLink v-for="item in nav" :key="item.name" :to="{ name: item.name }" :class="{ 'section-active': activeSection === item.name }" :aria-label="t(item.label)" :title="t(item.label)">
          <AppIcon :name="item.icon" /><span>{{ t(item.label) }}</span>
        </RouterLink>
      </nav>
      <nav class="personal-nav" :aria-label="t('Your games')">
        <small class="nav-label">{{ t('YOUR SPACE') }}</small>
        <RouterLink v-for="item in shortcuts" :key="item.name" :to="{ name: item.name }" :title="t(item.label)" :aria-label="t(item.label)"><AppIcon :name="item.icon" /><span>{{ t(item.label) }}</span></RouterLink>
      </nav>
      <div class="side-bottom">
        <RouterLink :to="{ name: 'settings' }" :title="t('Settings')" :aria-label="t('Settings')"><AppIcon name="settings" /><span>{{ t('Settings') }}</span></RouterLink>
        <div class="sidebar-appearance"><ThemePicker compact /></div>
        <RouterLink class="sidebar-account" :to="{ name: auth.user ? 'profile' : 'login' }" :aria-label="auth.user?.nickname ?? t('Sign in')">
          <span class="avatar-button">{{ (auth.user?.nickname ?? 'G').slice(0, 1).toUpperCase() }}</span>
          <span class="account-copy"><strong>{{ auth.user?.nickname ?? t('Your next adventure') }}</strong><small>{{ t(auth.user ? 'My GameGlee' : 'Sign in to play') }}</small></span>
          <AppIcon name="chevron" :size="16" />
        </RouterLink>
      </div>
    </aside>

    <header v-if="!fullscreen" class="desktop-bar">
      <span class="desktop-location">{{ t(String(route.meta.title ?? 'GameGlee')) }}<span> / GameGlee</span></span>
      <RouterLink class="desktop-search" :to="{ name: 'search' }"><AppIcon name="search" :size="18" /><span>{{ t('Find your next game') }}</span><AppIcon name="arrow-right" :size="16" /></RouterLink>
      <RouterLink class="icon-button" :to="{ name: 'inbox' }" :aria-label="t('Inbox')"><AppIcon name="inbox" :size="20" /></RouterLink>
    </header>
    <header v-if="!fullscreen && route.name === 'home'" class="top-bar">
      <RouterLink :to="{ name: 'home' }" :aria-label="t('GameGlee home')"><BrandMark /></RouterLink>
      <RouterLink class="icon-button" :to="{ name: 'search' }" :aria-label="t('Search')"><AppIcon name="search" :size="21" /></RouterLink>
    </header>

    <main id="main-content" class="app-main" tabindex="-1"><RouterView :key="auth.sessionId" /></main>

    <nav v-if="!fullscreen" class="bottom-nav" :aria-label="t('Primary navigation')">
      <RouterLink v-for="item in nav" :key="item.name" :to="{ name: item.name }" :class="{ 'section-active': activeSection === item.name }">
        <span><AppIcon :name="item.icon" :size="21" /></span><small>{{ t(item.label) }}</small>
      </RouterLink>
    </nav>
  </div>
</template>
